import Phaser from 'phaser'
import GameScene from './scenes/GameScene.js'

const IS_POKI = import.meta.env.VITE_BUILD === 'poki'
const IS_CRAZY = import.meta.env.VITE_BUILD === 'crazy'

// Tiny helper to load Poki (or bail fast)
function loadPokiSDK(timeoutMs = 3000) {
  return new Promise((resolve) => {
    if (!IS_POKI && !IS_CRAZY) return resolve(null)

    const script = document.createElement('script')
    script.src = IS_POKI
      ? 'https://game-cdn.poki.com/scripts/v2/poki-sdk.js'
      : 'https://sdk.crazygames.com/crazygames-sdk-v3.js'
    script.async = true

    const done = (sdkOrNull) => {
      clearTimeout(timer)
      resolve(sdkOrNull ?? null)
    }

    script.onload = () => {
      // If the script loaded but the global isn't present, treat as failure
      const sdk = IS_POKI ? window.PokiSDK : window.CrazyGamesSDK
      done(sdk || null)
    }
    script.onerror = () => done(null)

    const timer = setTimeout(() => {
      console.warn('[POKI] load timeout')
      done(null)
    }, timeoutMs)

    document.head.appendChild(script)
  })
}

async function boot() {
  await loadPokiSDK()

  const config = {
    type: Phaser.AUTO,
    parent: 'phaser-root',
    backgroundColor: '#3bc2ff',
    render: { roundPixels: true, pixelArt: false },
    scale: {
      mode: Phaser.Scale.RESIZE, // dynamically resize to fill the entire page
      // mode: Phaser.Scale.FIT, // scale to fit the screen
      // autoCenter: Phaser.Scale.CENTER_BOTH,
      autoCenter: Phaser.Scale.NO_CENTER,
      width: window.innerWidth,
      height: window.innerHeight,
      // width: 1280,
      // height: 720,
      resolution: window.devicePixelRatio, // crisp on HiDPI
    },
    physics: {
      default: 'arcade',
      arcade: { gravity: { y: 0 }, debug: false },
    },
    input: {
      touch: {
        capture: true,
        target: 'phaser-root',
      },
      activePointers: 3, // Support multi-touch
    },
    scene: [GameScene],
  }

  const game = new Phaser.Game(config)

  // Hook up HTML UI to the scene once it's running
  function withScene(cb) {
    const tryGet = () => {
      const scene = game.scene.keys['GameScene']
      if (scene && scene.sys.isActive()) {
        cb(scene)
        return
      }
      requestAnimationFrame(tryGet)
    }
    tryGet()
  }

  // HTML UI events
  withScene((scene) => {
    // Shop
    //   buyBtn.addEventListener('click', () => scene.buySeed())
    const buyBtn = document.getElementById('buySeed')
    buyBtn.addEventListener('click', () => {
      scene.refreshShop()
      shop.style.display = 'block'
    })
    const shop = document.getElementById('seedShop')
    const closeShop = document.getElementById('closeShop')
    shop.addEventListener('click', (e) => {
      if (scene.tutorialFinished === false) return
      if (e.target === shop) shop.style.display = 'none'
    })
    closeShop.addEventListener('click', () => {
      if (scene.tutorialFinished === false && scene.tutorialStep < 4) return
      shop.style.display = 'none'
    })

    // Inventory
    const invBtn = document.getElementById('inventoryBtn')
    const inventory = document.getElementById('inventoryModal')
    const close = document.getElementById('closeInventory')
    invBtn.addEventListener('click', () => {
      if (scene.tutorialFinished === false && scene.tutorialStep < 6) return
      scene.refreshInventoryHTML()
      inventory.style.display = 'block'
    })
    close.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      inventory.style.display = 'none'
    })
    inventory.addEventListener('click', (e) => {
      if (scene.tutorialFinished === false) return
      if (e.target === inventory) inventory.style.display = 'none'
    })
    const equipBest = document.getElementById('equipBest')
    equipBest.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      scene.equipBest()
      inventory.style.display = 'none'
    })
    const sellAll = document.getElementById('sellAll')
    sellAll.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      scene.sellAll()
      inventory.style.display = 'none'
    })

    // Index
    const indexBtn = document.getElementById('indexBtn')
    const indexModal = document.getElementById('indexModal')
    const closeIndex = document.getElementById('closeIndex')
    indexBtn.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      scene.refreshIndex()
      indexModal.style.display = 'block'
    })
    closeIndex.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      indexModal.style.display = 'none'
    })
    const animalIndexTab = document.getElementById('animalIndexTab')
    const plantIndexTab = document.getElementById('plantIndexTab')
    animalIndexTab.addEventListener('click', () => {
      animalIndexTab.classList.add('selected')
      plantIndexTab.classList.remove('selected')
      scene.ui.indexTab = 0
      scene.ui.indexMutation = 'Normal'
      scene.refreshIndex()
    })
    plantIndexTab.addEventListener('click', () => {
      plantIndexTab.classList.add('selected')
      animalIndexTab.classList.remove('selected')
      scene.ui.indexTab = 1
      scene.ui.indexMutation = 'Normal'
      scene.refreshIndex()
    })

    // Toolbar
    const shovelBtn = document.getElementById('shovelBtn')
    shovelBtn.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      shovelBtn.classList.toggle('selected')
      scene.shovel = !scene.shovel
      // Deactivate weapon when shovel is activated
      if (scene.shovel && scene.weapon) {
        scene.weapon = false
        weaponBtn.classList.remove('selected')
      }
    })

    const weaponBtn = document.getElementById('weaponBtn')
    weaponBtn.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      weaponBtn.classList.toggle('selected')
      scene.weapon = !scene.weapon
      // Deactivate shovel when weapon is activated
      if (scene.weapon && scene.shovel) {
        scene.shovel = false
        shovelBtn.classList.remove('selected')
      }
    })

    const sellPlantsBtn = document.getElementById('sellPlantsBtn')
    sellPlantsBtn.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      scene.sellAllPlants()
    })

    const volumeBtn = document.getElementById('volumeBtn')

    // Rebirth
    const rebirthBtn = document.getElementById('rebirthBtn')
    const rebirthModal = document.getElementById('rebirthModal')
    const closeRebirth = document.getElementById('closeRebirth')
    rebirthBtn.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      scene.refreshRebirth()
      rebirthModal.style.display = 'block'
    })
    closeRebirth.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      rebirthModal.style.display = 'none'
    })
    const rebirthConfirm = document.getElementById('rebirthConfirm')
    const confirmRebirth = document.getElementById('confirmRebirth')
    confirmRebirth.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      scene.performRebirth()
      rebirthConfirm.style.display = 'none'
      rebirthModal.style.display = 'none'
    })
    const cancelRebirth = document.getElementById('cancelRebirth')
    cancelRebirth.addEventListener('click', () => {
      if (scene.tutorialFinished === false) return
      rebirthConfirm.style.display = 'none'
      rebirthModal.style.display = 'none'
    })

    // allow scene to update UI text/disable states
    scene.setUIBindings({
      moneyEl: document.getElementById('moneyAmount'),
      buyBtn,
      shop,
      shopList: document.getElementById('seedShopList'),
      seedInventory: document.getElementById('seedInventory'),
      inventoryBtn: invBtn,
      inventory,
      inventoryListEl: document.getElementById('animalInventory'),
      indexListEl: document.getElementById('animalIndex'),
      indexTabs: document.getElementById('animalIndexTabs'),
      animalIndexTab,
      plantIndexTab,
      shovelBtn,
      weaponBtn,
      sellPlantsBtn,
      volumeBtn,
      volumeHighIcon: document.getElementById('volumeHigh'),
      volumeMuteIcon: document.getElementById('volumeMute'),
      rebirthModalBody: document.getElementById('rebirthModalBody'),
    })
  })
}

boot()
